import { useState, useEffect } from "react";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import CommonForm from "../../../../components/form/form";
import DefaultButton from "../../../../components/buttons/defaultButton";

const ApproverConfigSection = ({ designations, onSubmit, initialData }) => {
  const [selectFields, setSelectFields] = useState([{ id: Date.now(), value: "" }]);
  const [selectedValues, setSelectedValues] = useState([]);

  // Initialize selectFields from initialData when it's available
  useEffect(() => {
    // Wait for both initialData and designations to be available
    if (initialData && initialData.designations && Array.isArray(initialData.designations) && initialData.designations.length > 0 && designations && designations.length > 0) {
      // Convert designation IDs to selectFields format
      // Use id (not uuid) for option values
      const fields = initialData.designations.map((designationId, index) => {
        // Find the matching designation to get the id
        const matchingDesignation = designations.find(
          (d) => String(d.id) === String(designationId) || String(d.uuid) === String(designationId)
        );
        
        // Always use id (not uuid)
        const valueToUse = matchingDesignation 
          ? matchingDesignation.id
          : String(designationId);
        
        return {
          id: Date.now() + index,
          value: String(valueToUse), // Ensure it's a string for select value
        };
      });
      setSelectFields(fields);
      setSelectedValues(fields.map(field => field.value));
    } else if (initialData !== null && (!initialData.designations || initialData.designations.length === 0)) {
      // If initialData exists but no designations, reset to empty
      setSelectFields([{ id: Date.now(), value: "" }]);
      setSelectedValues([]);
    }
    // If initialData is null (not loaded yet), don't reset - keep current state
  }, [initialData, designations]);

  // Update selectedValues array whenever any select value changes
  useEffect(() => {
    const values = selectFields
      .map((field) => field.value)
      .filter((value) => value !== "");
    setSelectedValues(values);
  }, [selectFields]);

  const handleAddSelect = () => {
    setSelectFields([...selectFields, { id: Date.now(), value: "" }]);
  };

  const handleRemoveSelect = (id) => {
    if (selectFields.length > 1) {
      setSelectFields(selectFields.filter((field) => field.id !== id));
    }
  };

  const handleSelectChange = (id, value) => {
    setSelectFields(
      selectFields.map((field) =>
        field.id === id ? { ...field, value } : field
      )
    );
  };

  // Get selected designation names for display
  const getSelectedDesignationNames = () => { 
    return selectedValues
      .map((id) => {
        // Match by id only (not uuid)
        const designation = designations.find((d) => String(d.id) === String(id));
 
        return designation ? designation.name : null;
      })
      .filter(Boolean);
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Section Description */}
      <div className="bg-information-50 border border-information-200 rounded-lg p-4">
        <h4 className="text-theme font-semibold mb-2 flex items-center gap-2">
          <span>👥</span> About Approver Configuration
        </h4>
        <p className="text-sm text-theme mb-2">
          Configure who approves leave applications and how the approval process
          works. Set default approval flows and delegation rules.
        </p>
        <div className="bg-white rounded p-3 mt-2">
          <p className="text-xs font-medium text-theme mb-1">
            Example Scenario:
          </p>
          <p className="text-xs text-textLight">
            If you set "Manager + HR" flow: Employee applies for leave → Direct
            Manager receives notification and approves → HR receives
            notification and gives final approval → Leave is approved. If
            Manager rejects, HR never receives it.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme mb-2">
          Approver Configuration
        </h3>
        <p className="text-sm text-textLight mb-4">
          Define the default approval workflow for all leave applications. This
          can be overridden for specific employees or leave types if needed.
        </p>

        {/* Designation Selection Section */}
        <div className="bg-light rounded-lg p-4 mb-4">
          <label className="block text-sm font-medium text-theme mb-3">
            Select Designation(s) for Approval Flow
          </label>
          <p className="text-xs text-textLight mb-4">
            Add multiple designations to create a sequential approval flow. The order matters - approvals will flow from first to last designation.
          </p>
          
          <div className="space-y-3">
            {selectFields.map((field, index) => (
              <div key={field.id} className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-sm font-medium text-theme min-w-[60px]">
                      Step {index + 1}:
                    </span>
                    <select
                      id={`designation-select-${field.id}`}
                      name={`designation-${field.id}`}
                      value={field.value}
                      onChange={(e) => handleSelectChange(field.id, e.target.value)}
                      className="flex-1 px-3 py-2 border border-borderColor rounded-md bg-white text-theme focus:outline-none focus:ring-2 focus:ring-theme"
                    >
                      <option value="">Select a designation</option>
                      {designations
                        .filter(
                          (option) =>
                            !selectFields.some(
                              (f) => f.id !== field.id && String(f.value) === String(option.id)
                            )
                        )
                        .map((option) => {
                          // Always use id (not uuid)
                          return (
                            <option key={option.id || option.uuid} value={String(option.id)}>
                              {option.name}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  {selectFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveSelect(field.id)}
                      className="flex items-center justify-center w-10 h-10 border border-red-300 rounded-md bg-white text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                      title="Remove this designation"
                    >
                      <IoMdClose className="w-5 h-5" />
                    </button>
                  )}
                  {index === selectFields.length - 1 && (
                    <button
                      type="button"
                      onClick={handleAddSelect}
                      className="flex items-center justify-center w-10 h-10 border border-borderColor rounded-md bg-white text-theme hover:bg-light focus:outline-none focus:ring-2 focus:ring-theme transition-colors"
                      title="Add another designation"
                    >
                      <IoMdAdd className="w-5 h-5" />
                    </button>
                  )}
                </div>
            ))}
          </div>
        </div>

        {/* Selected Designations Display */}
        {selectedValues.length > 0 && (
          <div className="bg-success-50 border border-success-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-theme mb-2">
              ✅ Current Approval Flow:
            </p>
            <div className="flex flex-wrap gap-2">
              {getSelectedDesignationNames().map((name, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-success-300"
                >
                  <span className="text-xs font-medium text-success-700">
                    {index + 1}.
                  </span>
                  <span className="text-sm text-theme">{name}</span>
                  {index < getSelectedDesignationNames().length - 1 && (
                    <span className="text-success-600 mx-1">→</span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-textLight mt-2">
              Selected values: [{selectedValues.join(", ")}]
            </p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <DefaultButton
            variant="primary"
            name="Save Approval Configuration"
            onClick={() => {
              if (onSubmit) {
                onSubmit({ 
                  designations: selectedValues,
                  allow_delegation: initialData?.allow_delegation || false,
                  default_approver_fallback: initialData?.default_approver_fallback || "",
                });
              }
            }}
            disabled={selectedValues.length === 0}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme mb-2">
          Delegation Rules
        </h3>
        <p className="text-sm text-textLight mb-4">
          Configure delegation rules for leave approvals. When enabled,
          approvers can delegate their approval authority to other users when
          they are unavailable.
        </p>
        <div className="bg-success-50 border border-success-200 rounded p-3 mb-4">
          <p className="text-xs font-medium text-theme mb-1">✅ Impact:</p>
          <p className="text-xs text-textLight">
            When delegation is enabled: If Manager A is on leave, they can
            delegate approval authority to Manager B. All leave applications
            that would normally go to Manager A will now go to Manager B.
            Delegation can be temporary (specific dates) or permanent.
          </p>
        </div>
        <div className="bg-light p-4 rounded-md border border-borderColor">
          <p className="text-sm text-theme mb-2 font-medium">
            Delegation Features:
          </p>
          <ul className="text-sm text-textLight space-y-1 list-disc list-inside">
            <li>Managers can delegate to deputies or team leads</li>
            <li>Delegation can be date-specific or ongoing</li>
            <li>Delegated approvers receive all notifications</li>
            <li>Original approver can revoke delegation anytime</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ApproverConfigSection;
